import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Image as ImageIcon, Palette, ExternalLink } from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as tenantsApi from '@/api/tenants';

import type { FPATenant } from '../FPASettingsHub';

interface BrandingTabProps {
  tenantId: number;
  tenant: FPATenant | null;
  onSaved: () => Promise<void> | void;
}

/**
 * Tenant branding — logo URL + primary colour, surfaced on every
 * tenant-scoped email and inside the FPA Hub itself. Phase 1 keeps
 * the logo as a public URL (Imgur, the FPA's own S3 bucket, etc.);
 * S3 upload is a Phase 2 follow-up.
 */
export default function BrandingTab({ tenantId, tenant, onSaved }: BrandingTabProps) {
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogoUrl(tenant?.logo_url ?? '');
    setPrimaryColor(tenant?.primary_color ?? '');
  }, [tenant]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await tenantsApi.updateTenant(tenantId, {
        logo_url: logoUrl.trim() || null,
        primary_color: primaryColor.trim() || null,
      });
      toast.success('Branding saved');
      await onSaved();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isHexLike = (v: string) =>
    !v || /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(v);

  return (
    <div className="space-y-4">
      <Section
        title={
          <span className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            Logo
          </span>
        }
        description="Shown in the header of every email we send for your FPA, and on the FPA Hub."
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="logo-url">Logo URL</Label>
            <Input
              id="logo-url"
              placeholder="https://example.com/logo.png"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Public URL of a PNG / SVG. We recommend a transparent background
              and a height around 60–80px. File-upload support lands in Phase 2.
            </p>
          </div>

          {logoUrl && (
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Preview
              </p>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={logoUrl}
                alt="Logo preview"
                className="max-h-16 max-w-full"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                <ExternalLink className="mr-1 inline h-3 w-3" />
                {logoUrl}
              </p>
            </div>
          )}
        </div>
      </Section>

      <Section
        title={
          <span className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-muted-foreground" />
            Primary colour
          </span>
        }
        description="Used as the accent colour in email headers and buttons."
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="primary-color">Hex colour</Label>
              <Input
                id="primary-color"
                placeholder="#e64a19"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Picker</Label>
              <input
                type="color"
                value={primaryColor.startsWith('#') ? primaryColor : '#1f2937'}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-9 w-16 cursor-pointer rounded-md border bg-background"
              />
            </div>
          </div>

          {!isHexLike(primaryColor) && (
            <Alert variant="destructive">
              <AlertDescription>
                Use a hex value like <code>#e64a19</code>.
              </AlertDescription>
            </Alert>
          )}

          {primaryColor && isHexLike(primaryColor) && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Preview</p>
              <div
                className="rounded-md p-4 text-white"
                style={{
                  backgroundColor: primaryColor.startsWith('#')
                    ? primaryColor
                    : `#${primaryColor}`,
                }}
              >
                Sample email header — your FPA branding in action.
              </div>
            </div>
          )}
        </div>
      </Section>

      <div className="flex justify-end gap-2">
        <Button
          onClick={handleSave}
          disabled={saving || !isHexLike(primaryColor)}
          className="gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save branding'}
        </Button>
      </div>
    </div>
  );
}
